import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle, XCircle, Loader2, SkipForward } from 'lucide-react';
import { pinyinEquivalent } from '../utils/pinyin';

/**
 * Shown after the user picks "Again" or "Hard".
 * Prompts them to type the meaning (and pinyin for Chinese) to reinforce recall,
 * then reveals the correct answer and proceeds.
 *
 * Props:
 *   word:        the target word being reviewed
 *   languageCode:'zh', 'es', etc.
 *   definitions: [{definition, pos}, ...]
 *   correctPinyin: string (optional)
 *   onContinue:  () => void  called when user clicks Continue
 */
export default function RecallPrompt({
  word,
  languageCode,
  definitions = [],
  correctPinyin = '',
  onContinue,
}) {
  const isChinese = languageCode === 'zh' || languageCode === 'zho';
  const [meaning, setMeaning] = useState('');
  const [pinyinInput, setPinyinInput] = useState('');
  const [checked, setChecked] = useState(false);
  const [meaningResult, setMeaningResult] = useState(null); // true/false/null
  const [pinyinResult, setPinyinResult] = useState(null);
  const meaningRef = useRef(null);

  useEffect(() => {
    // Auto-focus first input
    if (meaningRef.current) meaningRef.current.focus();
  }, []);

  const firstDef = definitions?.[0]?.definition || definitions?.[0] || '';

  const checkAnswer = () => {
    // Meaning: lenient substring match (case-insensitive, strip punctuation)
    const norm = (s) => (s || '').toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
    const userNorm = norm(meaning);

    let meaningOk = false;
    if (userNorm) {
      for (const d of definitions || []) {
        const def = norm(typeof d === 'string' ? d : d.definition);
        if (!def) continue;
        // Accept if user's answer contains a key word from the definition or vice-versa
        const userTokens = userNorm.split(' ').filter((t) => t.length >= 2);
        const defTokens = def.split(' ').filter((t) => t.length >= 2);
        // At least one overlapping content word
        if (userTokens.some((t) => defTokens.includes(t)) || def.includes(userNorm) || userNorm.includes(def.substring(0, 20))) {
          meaningOk = true;
          break;
        }
      }
    }
    setMeaningResult(meaningOk);

    if (isChinese) {
      setPinyinResult(pinyinEquivalent(pinyinInput, correctPinyin));
    }
    setChecked(true);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !checked) {
      checkAnswer();
    } else if (e.key === 'Enter' && checked) {
      onContinue();
    }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="recall-prompt">
      <div className="text-center">
        <p className="text-sm text-muted-foreground mb-1">Try to recall before moving on</p>
        <h3 className="text-3xl font-bold font-reader">{word}</h3>
      </div>

      {isChinese && (
        <div>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">Pinyin</label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              ref={meaningRef}
              value={pinyinInput}
              onChange={(e) => setPinyinInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder='e.g. "ni hao" or "ni3 hao3" or "nǐ hǎo"'
              disabled={checked}
              data-testid="recall-pinyin-input"
              className="font-mono"
            />
            {checked && pinyinResult === true && <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />}
            {checked && pinyinResult === false && <XCircle className="h-5 w-5 text-red-500 shrink-0" />}
          </div>
          {checked && pinyinResult === false && correctPinyin && (
            <p className="text-xs text-muted-foreground mt-1">
              Correct: <span className="font-mono text-primary">{correctPinyin}</span>
            </p>
          )}
        </div>
      )}

      <div>
        <label className="text-xs uppercase tracking-wide text-muted-foreground">Meaning</label>
        <div className="flex items-center gap-2 mt-1">
          <Input
            ref={isChinese ? null : meaningRef}
            value={meaning}
            onChange={(e) => setMeaning(e.target.value)}
            onKeyDown={handleKey}
            placeholder="What does it mean in English?"
            disabled={checked}
            data-testid="recall-meaning-input"
          />
          {checked && meaningResult === true && <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />}
          {checked && meaningResult === false && <XCircle className="h-5 w-5 text-red-500 shrink-0" />}
        </div>
        {checked && meaningResult === false && firstDef && (
          <p className="text-xs text-muted-foreground mt-1" data-testid="recall-correct-meaning">
            Correct: <span className="text-primary">{firstDef}</span>
          </p>
        )}
      </div>

      <div className="flex gap-2 justify-end pt-2">
        {!checked ? (
          <>
            <Button variant="ghost" onClick={onContinue} className="gap-1" data-testid="recall-skip-btn">
              <SkipForward className="h-4 w-4" /> Skip
            </Button>
            <Button onClick={checkAnswer} disabled={!meaning.trim() && !(isChinese && pinyinInput.trim())} data-testid="recall-check-btn">
              Check
            </Button>
          </>
        ) : (
          <Button onClick={onContinue} className="gap-1" data-testid="recall-continue-btn">
            Continue <Loader2 className="h-4 w-4 hidden" />
          </Button>
        )}
      </div>
    </div>
  );
}
